/**
 * PrivateRoute Guard
 * Protects routes that require authentication and role-based access
 */

import { Navigate, useLocation } from 'react-router-dom';
import { PATHS } from '../constants/paths';
import { isAuthenticated, canAccessRoute, getDefaultDashboardPath } from '../utils/auth';
import type { RouteMeta } from '../types';
import { MainLayout } from '../../components/layout/MainLayout';

interface PrivateRouteProps {
  children: React.ReactNode;
  meta?: RouteMeta;
}

/**
 * Protected route wrapper that checks authentication and authorization
 * @param children - Child components to render if authorized
 * @param meta - Route metadata including allowedRoles
 */
export const PrivateRoute = ({ children, meta }: PrivateRouteProps): JSX.Element => {
  const location = useLocation();
  
  // Check if user is authenticated
  const isAuth = isAuthenticated();
  if (!isAuth) {
    return <Navigate to={PATHS.LOGIN} replace state={{ from: location }} />;
  }

  // Check role-based access
  if (meta?.allowedRoles && meta.allowedRoles.length > 0) {
    const canAccess = canAccessRoute(meta.allowedRoles);
    if (!canAccess) {
      // User doesn't have access, redirect to their default dashboard based on role and profile status
      const defaultPath = getDefaultDashboardPath();
      return <Navigate to={defaultPath} replace />;
    }
  }

  return (
    <MainLayout>
      {children}
    </MainLayout>
  );
};
